import { colorWithAlpha, toTradeNumber } from "./chartOverlayUtils";

export const DRAWING_HIT_TOLERANCE_PX = 8;
export const DRAWING_HANDLE_RADIUS_PX = 5;
export const DRAWING_MIN_DRAG_PX = 12;

const finiteTime = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const canonicalAnchor = (value) => {
  if (!value || typeof value !== "object") return null;
  const time = finiteTime(value.time ?? value.timestamp);
  const price = toTradeNumber(value.price ?? value.value);
  if (time == null || price == null) return null;
  return { time, price };
};

const overlayAnchor = (overlay, endpoint) =>
  canonicalAnchor(
    overlay?.[`${endpoint}_anchor`] ||
      overlay?.[`${endpoint}Anchor`] ||
      {
        time: overlay?.[`${endpoint}_time`] ?? overlay?.[`${endpoint}Time`],
        price: overlay?.[`${endpoint}_price`] ?? overlay?.[`${endpoint}Price`],
      },
  );

const nearestTimeIndex = (priceTimes, rawTime) => {
  if (!Array.isArray(priceTimes) || priceTimes.length === 0) return null;
  const target = finiteTime(rawTime);
  if (target == null) return null;

  let low = 0;
  let high = priceTimes.length - 1;
  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const value = Number(priceTimes[middle]);
    if (value === target) return middle;
    if (value < target) low = middle + 1;
    else high = middle - 1;
  }

  const left = Math.max(0, Math.min(high, priceTimes.length - 1));
  const right = Math.max(0, Math.min(low, priceTimes.length - 1));
  return Math.abs(Number(priceTimes[left]) - target) <=
    Math.abs(Number(priceTimes[right]) - target)
    ? left
    : right;
};

const pointForAnchor = ({ chart, series, priceTimes, anchor }) => {
  const normalized = canonicalAnchor(anchor);
  if (!normalized) return null;
  const index = nearestTimeIndex(priceTimes, normalized.time);
  if (index == null) return null;

  const x = chart?.timeScale?.()?.logicalToCoordinate?.(index);
  const y = series?.priceToCoordinate?.(normalized.price);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return {
    x,
    y,
    index,
    time: Number(priceTimes[index]),
    price: normalized.price,
  };
};

const overlaysWithDraft = (overlays, draftOverlay) => {
  const rows = Array.isArray(overlays) ? overlays : [];
  if (!draftOverlay?.id) return rows;
  let replaced = false;
  const merged = rows.map((overlay) => {
    if (String(overlay?.id) !== String(draftOverlay.id)) return overlay;
    replaced = true;
    return { ...overlay, ...draftOverlay };
  });
  if (!replaced) merged.push(draftOverlay);
  return merged;
};

export const drawingAnchorForChartPoint = ({
  chart,
  series,
  priceTimes,
  x,
  y,
}) => {
  if (!Array.isArray(priceTimes) || priceTimes.length === 0) return null;
  const timeScale = chart?.timeScale?.();
  let logical = timeScale?.coordinateToLogical?.(x);
  if (!Number.isFinite(logical)) {
    const visibleRange = timeScale?.getVisibleLogicalRange?.();
    const plotWidth = Number(timeScale?.width?.());
    if (
      visibleRange &&
      Number.isFinite(visibleRange.from) &&
      Number.isFinite(visibleRange.to) &&
      Number.isFinite(plotWidth) &&
      plotWidth > 0
    ) {
      logical =
        visibleRange.from +
        (Math.max(0, Math.min(Number(x), plotWidth)) / plotWidth) *
          (visibleRange.to - visibleRange.from);
    }
  }
  const price = Number(series?.coordinateToPrice?.(y));
  if (!Number.isFinite(logical) || !Number.isFinite(price)) return null;

  const index = Math.max(
    0,
    Math.min(Math.round(logical), priceTimes.length - 1),
  );
  const time = Number(priceTimes[index]);
  if (!Number.isFinite(time)) return null;
  return { index, time, price };
};

export const buildEditableOverlayGeometry = ({
  chart,
  series,
  overlays,
  priceTimes,
  width,
  height,
  draftOverlay = null,
}) => {
  if (!chart || !series || !Number.isFinite(width) || !Number.isFinite(height)) {
    return [];
  }

  const timeScale = chart.timeScale?.();
  const plotWidth = Math.max(
    0,
    Math.min(Number(timeScale?.width?.()) || width, width),
  );

  return overlaysWithDraft(overlays, draftOverlay).reduce((geometry, overlay) => {
        if (!overlay || typeof overlay !== "object" || !overlay.id) return geometry;
        const kind = String(overlay.kind || "").toLowerCase();
        const color = overlay.color || "#94a3b8";

        if (kind === "horizontal_line") {
          const price = toTradeNumber(overlay.price ?? overlay.value);
          const y = price == null ? null : series.priceToCoordinate?.(price);
          if (!Number.isFinite(y)) return geometry;
          geometry.push({
            id: String(overlay.id),
            kind,
            overlay,
            color,
            x1: 0,
            x2: plotWidth,
            y1: y,
            y2: y,
          });
          return geometry;
        }

        if (kind === "price_zone" || kind === "price_zone_box") {
          const topPrice = toTradeNumber(overlay.top ?? overlay.upper ?? overlay.high);
          const bottomPrice = toTradeNumber(
            overlay.bottom ?? overlay.lower ?? overlay.low,
          );
          const yTop =
            topPrice == null ? null : series.priceToCoordinate?.(topPrice);
          const yBottom =
            bottomPrice == null ? null : series.priceToCoordinate?.(bottomPrice);
          if (!Number.isFinite(yTop) || !Number.isFinite(yBottom)) return geometry;
          geometry.push({
            id: String(overlay.id),
            kind: "price_zone",
            overlay,
            color,
            x1: 0,
            x2: plotWidth,
            y1: Math.min(yTop, yBottom),
            y2: Math.max(yTop, yBottom),
          });
          return geometry;
        }

        if (kind === "trend_line") {
          const start = pointForAnchor({
            chart,
            series,
            priceTimes,
            anchor: overlayAnchor(overlay, "start"),
          });
          const end = pointForAnchor({
            chart,
            series,
            priceTimes,
            anchor: overlayAnchor(overlay, "end"),
          });
          if (!start || !end) return geometry;
          geometry.push({
            id: String(overlay.id),
            kind,
            overlay,
            color,
            x1: start.x,
            y1: start.y,
            x2: end.x,
            y2: end.y,
            start,
            end,
          });
        }
        return geometry;
    }, []);
};

const pointDistance = (left, right) =>
  Math.hypot(left.x - right.x, left.y - right.y);

const segmentDistance = (point, start, end) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const denominator = dx * dx + dy * dy;
  if (denominator <= 0) return pointDistance(point, start);
  const projection = Math.max(
    0,
    Math.min(
      1,
      ((point.x - start.x) * dx + (point.y - start.y) * dy) / denominator,
    ),
  );
  return pointDistance(point, {
    x: start.x + projection * dx,
    y: start.y + projection * dy,
  });
};

export const hitTestEditableOverlay = ({
  geometry,
  x,
  y,
  tolerance = DRAWING_HIT_TOLERANCE_PX,
  selectedId = null,
}) => {
  const point = { x, y };
  const rows = [...(Array.isArray(geometry) ? geometry : [])].sort((left, right) => {
    if (String(left.id) === String(selectedId)) return 1;
    if (String(right.id) === String(selectedId)) return -1;
    return 0;
  });

  for (let index = rows.length - 1; index >= 0; index -= 1) {
    const item = rows[index];
    if (item.kind === "trend_line") {
      if (
        pointDistance(point, { x: item.x1, y: item.y1 }) <=
        tolerance + DRAWING_HANDLE_RADIUS_PX
      ) {
        return { item, handle: "start" };
      }
      if (
        pointDistance(point, { x: item.x2, y: item.y2 }) <=
        tolerance + DRAWING_HANDLE_RADIUS_PX
      ) {
        return { item, handle: "end" };
      }
      if (
        segmentDistance(
          point,
          { x: item.x1, y: item.y1 },
          { x: item.x2, y: item.y2 },
        ) <= tolerance
      ) {
        return { item, handle: "body" };
      }
      continue;
    }

    if (item.kind === "horizontal_line") {
      if (
        x >= item.x1 - tolerance &&
        x <= item.x2 + tolerance &&
        Math.abs(y - item.y1) <= tolerance
      ) {
        return { item, handle: "body" };
      }
      continue;
    }

    if (item.kind === "price_zone") {
      if (
        x >= item.x1 - tolerance &&
        x <= item.x2 + tolerance &&
        Math.abs(y - item.y1) <= tolerance
      ) {
        return { item, handle: "top" };
      }
      if (
        x >= item.x1 - tolerance &&
        x <= item.x2 + tolerance &&
        Math.abs(y - item.y2) <= tolerance
      ) {
        return { item, handle: "bottom" };
      }
      if (
        x >= item.x1 &&
        x <= item.x2 &&
        y >= item.y1 &&
        y <= item.y2
      ) {
        return { item, handle: "body" };
      }
    }
  }

  return null;
};

const clampedIndexDelta = (indexes, requestedDelta, priceTimes) => {
  if (!Array.isArray(priceTimes) || priceTimes.length === 0) return 0;
  const minimumIndex = Math.min(...indexes);
  const maximumIndex = Math.max(...indexes);
  return Math.max(
    -minimumIndex,
    Math.min(requestedDelta, priceTimes.length - 1 - maximumIndex),
  );
};

export const moveEditableOverlay = ({
  overlay,
  handle,
  dragStart,
  current,
  priceTimes,
}) => {
  if (!overlay || !dragStart || !current) return overlay;
  const kind = String(overlay.kind || "").toLowerCase();

  if (kind === "horizontal_line") {
    return { ...overlay, price: current.price };
  }

  if (kind === "price_zone" || kind === "price_zone_box") {
    const top = toTradeNumber(overlay.top ?? overlay.upper ?? overlay.high);
    const bottom = toTradeNumber(overlay.bottom ?? overlay.lower ?? overlay.low);
    if (top == null || bottom == null) return overlay;
    if (handle === "top") {
      return {
        ...overlay,
        top: Math.max(current.price, bottom),
        bottom: Math.min(current.price, bottom),
      };
    }
    if (handle === "bottom") {
      return {
        ...overlay,
        top: Math.max(top, current.price),
        bottom: Math.min(top, current.price),
      };
    }
    const priceDelta = current.price - dragStart.price;
    return {
      ...overlay,
      top: top + priceDelta,
      bottom: bottom + priceDelta,
    };
  }

  if (kind !== "trend_line") return overlay;
  const start = overlayAnchor(overlay, "start");
  const end = overlayAnchor(overlay, "end");
  if (!start || !end) return overlay;

  if (handle === "start") {
    return {
      ...overlay,
      start_anchor: { time: current.time, price: current.price },
    };
  }
  if (handle === "end") {
    return {
      ...overlay,
      end_anchor: { time: current.time, price: current.price },
    };
  }

  const startIndex = nearestTimeIndex(priceTimes, start.time);
  const endIndex = nearestTimeIndex(priceTimes, end.time);
  if (startIndex == null || endIndex == null) return overlay;
  const requestedDelta = current.index - dragStart.index;
  const indexDelta = clampedIndexDelta(
    [startIndex, endIndex],
    requestedDelta,
    priceTimes,
  );
  const priceDelta = current.price - dragStart.price;
  return {
    ...overlay,
    start_anchor: {
      time: Number(priceTimes[startIndex + indexDelta]),
      price: start.price + priceDelta,
    },
    end_anchor: {
      time: Number(priceTimes[endIndex + indexDelta]),
      price: end.price + priceDelta,
    },
  };
};

export const drawingGeometryLength = (item) => {
  if (!item) return 0;
  return Math.hypot(item.x2 - item.x1, item.y2 - item.y1);
};

const drawHandle = (context, x, y, color) => {
  context.beginPath();
  context.arc(x, y, DRAWING_HANDLE_RADIUS_PX, 0, Math.PI * 2);
  context.fillStyle = "#ffffff";
  context.fill();
  context.lineWidth = 2;
  context.strokeStyle = color;
  context.stroke();
};

export const drawEditableOverlayGeometry = ({
  context,
  geometry,
  selectedId = null,
  compactLabels = false,
}) => {
  if (!context) return;

  (Array.isArray(geometry) ? geometry : []).forEach((item) => {
    const selected = String(item.id) === String(selectedId);
    const color = item.color || "#94a3b8";

    if (item.kind === "price_zone") {
      const zoneHeight = Math.max(item.y2 - item.y1, 0);
      if (zoneHeight <= 0) return;
      context.save();
      context.fillStyle = colorWithAlpha(color, selected ? 0.18 : 0.13);
      context.strokeStyle = colorWithAlpha(color, selected ? 0.95 : 0.5);
      context.lineWidth = selected ? 2 : 1;
      context.setLineDash(selected ? [] : [6, 5]);
      context.fillRect(item.x1, item.y1, item.x2 - item.x1, zoneHeight);
      context.strokeRect(
        item.x1 + 0.5,
        item.y1 + 0.5,
        Math.max(item.x2 - item.x1 - 1, 0),
        Math.max(zoneHeight - 1, 0),
      );
      if (item.overlay.label) {
        context.setLineDash([]);
        context.fillStyle = color;
        context.font =
          "600 11px system-ui, -apple-system, BlinkMacSystemFont, sans-serif";
        context.textAlign = "right";
        context.textBaseline = "top";
        context.fillText(
          item.overlay.label,
          item.x2 - (compactLabels ? 78 : 8),
          item.y1 + 5,
        );
      }
      if (selected) {
        const handleX = Math.max(item.x1 + 18, item.x2 - 28);
        drawHandle(context, handleX, item.y1, color);
        drawHandle(context, handleX, item.y2, color);
      }
      context.restore();
      return;
    }

    if (item.kind === "trend_line") {
      context.save();
      context.strokeStyle = color;
      context.lineWidth = selected ? 3 : 2;
      context.lineCap = "round";
      context.setLineDash([]);
      context.beginPath();
      context.moveTo(item.x1, item.y1);
      context.lineTo(item.x2, item.y2);
      context.stroke();
      if (selected) {
        drawHandle(context, item.x1, item.y1, color);
        drawHandle(context, item.x2, item.y2, color);
      }
      context.restore();
      return;
    }

    if (item.kind === "horizontal_line" && selected) {
      context.save();
      context.strokeStyle = colorWithAlpha(color, 0.95);
      context.lineWidth = 3;
      context.setLineDash([]);
      context.beginPath();
      context.moveTo(item.x1, item.y1);
      context.lineTo(item.x2, item.y2);
      context.stroke();
      drawHandle(
        context,
        Math.max(item.x1 + 18, item.x2 - 28),
        item.y1,
        color,
      );
      context.restore();
    }
  });
};
